import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Minus,
  ArrowRight,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/fergus-alternative";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Fergus Alternative for UK Builders",
  "description": "Looking for a Fergus alternative for UK residential builders? The Proper Tool offers AI quote generation, a 31-category UK cost library, and a 14-day free trial from £29/mo.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Compare", "item": "https://thepropertool.com/comparisons" },
    { "@type": "ListItem", "position": 3, "name": "Fergus Alternative", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best Fergus alternative for UK residential builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For UK residential builders who need detailed construction quoting rather than field service scheduling, The Proper Tool is the strongest Fergus alternative. Fergus is a field service management platform built for trades with multiple technicians. The Proper Tool focuses on AI-powered construction quoting, a 31-category UK cost library, and structural drawing analysis, starting from £29/month with a 14-day free trial.",
      },
    },
    {
      "@type": "Question",
      "name": "Is Fergus designed for construction builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Fergus is a field service management platform primarily designed for trades like electricians, plumbers, and HVAC engineers with 5 or more field technicians. It covers job scheduling, time tracking, and invoicing rather than structured construction quoting. For residential builders doing extension, loft conversion, and alteration work, the fit is poor.",
      },
    },
    {
      "@type": "Question",
      "name": "How does Fergus pricing compare to The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Fergus pricing in the UK is enterprise-quoted for most tiers, with costs rising with team size. The Proper Tool starts at £29 per month with a 14-day free trial and no credit card required. For small builders and sole traders who need quoting software rather than a field service management platform, The Proper Tool delivers more relevant value.",
      },
    },
    {
      "@type": "Question",
      "name": "Can Fergus generate construction quotes from drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. Fergus does not read architectural drawings or structural plans. Its quoting module is designed for service job templates with predefined tasks and rates. The Proper Tool reads uploaded PDF drawings using AI, extracting beam sizes, floor areas, and dimensions to generate a more complete and accurate construction quote.",
      },
    },
    {
      "@type": "Question",
      "name": "Does Fergus have a UK construction pricing database?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Fergus does not have a construction pricing database calibrated for UK residential construction. Its pricing is geared toward field service jobs for electrical, plumbing, and HVAC trades. The Proper Tool has a 31-category UK cost library covering the full range of residential construction trade categories, from groundworks to finishing trades.",
      },
    },
  ],
};

type TriState = "yes" | "partial" | "no";

interface ComparisonRow {
  feature: string;
  fergus: string;
  fergusState: TriState;
  tpt: string;
  tptState: TriState;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: "Built for residential construction", fergus: "No: field service focus", fergusState: "no", tpt: "Yes: UK residential", tptState: "yes" },
  { feature: "AI scope generation from description", fergus: "No", fergusState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Reads structural drawings and PDFs", fergus: "No", fergusState: "no", tpt: "Yes: AI interpretation", tptState: "yes" },
  { feature: "UK residential construction pricing", fergus: "Not calibrated for construction", fergusState: "no", tpt: "Yes: 31 UK trade categories", tptState: "yes" },
  { feature: "Scope completeness validation", fergus: "No", fergusState: "no", tpt: "Yes: built-in expert review", tptState: "yes" },
  { feature: "Labour / materials / plant split", fergus: "Partial", fergusState: "partial", tpt: "Yes: per line item", tptState: "yes" },
  { feature: "Multi-technician scheduling", fergus: "Yes (core feature)", fergusState: "yes", tpt: "No (quote-focused)", tptState: "no" },
  { feature: "Job management and tracking", fergus: "Yes", fergusState: "yes", tpt: "No (quote-focused)", tptState: "no" },
  { feature: "Branded PDF quote output", fergus: "Yes", fergusState: "yes", tpt: "Yes", tptState: "yes" },
  { feature: "Free trial", fergus: "Limited", fergusState: "partial", tpt: "Yes: 14-day free trial", tptState: "yes" },
  { feature: "Starting price", fergus: "Enterprise-quoted", fergusState: "no", tpt: "£29/month (per account)", tptState: "yes" },
  { feature: "Credit card required to trial", fergus: "Yes", fergusState: "no", tpt: "No", tptState: "yes" },
];

function Cell({ value, state }: { value: string; state: TriState }) {
  if (state === "yes") return (
    <span className="flex items-start gap-1.5 text-green-600 dark:text-green-400 font-medium">
      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  if (state === "no") return (
    <span className="flex items-start gap-1.5 text-red-500 dark:text-red-400">
      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  return (
    <span className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
}

const WHY_LOOK = [
  {
    title: "Built for field service, not residential construction",
    desc: "Fergus is designed for trades with multiple field technicians managing recurring service jobs. Its quoting module reflects this: simple job templates for service calls, not structured scopes of works for extensions, loft conversions, or structural alterations.",
  },
  {
    title: "No AI quote generation from descriptions or drawings",
    desc: "Fergus requires manual job setup. There is no way to describe a building project in plain language and have the software generate a costed scope, and no ability to upload structural drawings and extract what needs quoting.",
  },
  {
    title: "Pricing database not calibrated for UK residential construction",
    desc: "Fergus pricing is geared toward service trade rates for electrical, plumbing, and HVAC work. UK residential construction categories including groundworks, structural steelwork, brickwork, and finishing trades are not its strength.",
  },
  {
    title: "Priced for multi-technician field service teams",
    desc: "Fergus is designed for businesses managing 5 or more field technicians. For small builders and sole traders who primarily need fast, accurate construction quoting, the platform is more software than the job requires.",
  },
];

const STAY_ON_FERGUS = [
  "You manage a team of 5 or more field technicians doing electrical, plumbing, or HVAC service work.",
  "Your business model is recurring service contracts and job scheduling rather than project-based residential construction.",
  "You have already built your workflows inside Fergus and have working templates in place.",
  "You need multi-technician dispatching and service contract management as your primary requirement.",
];

const SWITCH_REASONS = [
  "You are a residential builder doing extension, loft conversion, or structural alteration work, not a service trade.",
  "You want to generate a complete, costed construction scope from drawings or a project description.",
  "Your clients expect a detailed line-item scope of works, not a simple service job quote.",
  "You need UK residential construction pricing built in, not a service-trade rate card.",
  "You want to trial the software on real jobs without going through a sales process or paying upfront.",
  "You want to be quoting professionally within minutes, not after days of template setup.",
];

const FAQS = [
  {
    q: "What is the best Fergus alternative for UK residential builders?",
    a: "For UK residential builders who need detailed construction quoting, The Proper Tool is the strongest Fergus alternative. Fergus is a field service management platform built for trades with multiple technicians. The Proper Tool focuses on AI-powered construction quoting, a 31-category UK cost library, and structural drawing analysis, starting from £29/month with a 14-day free trial.",
  },
  {
    q: "Is Fergus designed for construction builders?",
    a: "Fergus is a field service management platform primarily designed for trades like electricians, plumbers, and HVAC engineers. It covers job scheduling, time tracking, and invoicing rather than structured construction quoting. For residential builders doing extension, loft conversion, and alteration work, the fit is poor.",
  },
  {
    q: "How does Fergus pricing compare to The Proper Tool?",
    a: "Fergus pricing in the UK is enterprise-quoted for most tiers, with costs rising with team size. The Proper Tool starts at £29 per month with a 14-day free trial and no credit card required.",
  },
  {
    q: "Can Fergus generate construction quotes from drawings?",
    a: "No. Fergus does not read architectural drawings or structural plans. The Proper Tool reads uploaded PDF drawings using AI, extracting beam sizes, floor areas, and dimensions to generate a more accurate and complete construction quote.",
  },
  {
    q: "Does Fergus have a UK construction pricing database?",
    a: "Fergus does not have a construction pricing database calibrated for UK residential construction. The Proper Tool has a 31-category UK cost library covering the full range of residential construction trade categories, from groundworks to finishing trades.",
  },
];

export default function FergusAlternativePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Fergus Alternative for UK Builders: Construction Quoting Software | The Proper Tool"
        description="Looking for a Fergus alternative built for UK residential builders? The Proper Tool offers AI quote generation, a 31-category UK cost library, and a 14-day free trial from £29/mo."
        canonical="/fergus-alternative"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Fergus Alternative</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Comparison</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Fergus Alternative for UK Builders
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Fergus is a field service platform built for multi-technician trades. If you are a UK residential builder who needs fast, accurate construction quoting, here is an honest comparison with The Proper Tool.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no credit card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#comparison-table">See the comparison</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · From £29/mo · No credit card required</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why UK builders look for a Fergus alternative</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Fergus is a well-regarded field service management platform. But residential builders consistently run into the same mismatches when they try to use it for construction quoting:
            </p>
            <div className="space-y-4">
              {WHY_LOOK.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="comparison-table">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Proper Tool vs Fergus: feature comparison</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A factual side-by-side comparison for UK residential builders.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">Fergus</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.feature}</td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.fergus} state={row.fergusState} /></td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.tpt} state={row.tptState} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
              Competitor pricing and feature information is based on publicly available data at time of writing. Prices and features may have changed. Verify directly with Fergus before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pricing comparison</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Fergus pricing in the UK is enterprise-quoted on most tiers, scaling with the number of technicians. For small builders and sole traders, the cost is difficult to justify when quoting is the primary need rather than multi-technician scheduling.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-border p-6 bg-muted/20">
                <h3 className="font-bold text-lg mb-3">Fergus</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Pricing enterprise-quoted by team size</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Not calibrated for UK residential construction</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />No AI scope generation or drawing interpretation</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Field service focus may require significant setup for builders</li>
                </ul>
              </div>
              <div className="rounded-xl border border-primary bg-primary/5 p-6">
                <h3 className="font-bold text-lg mb-3 text-primary">The Proper Tool</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />From £29/month (per account)</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />14-day free trial</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />No credit card required</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />UK cost library built in, first quote in under 10 minutes</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />No per-seat fees: one price for the whole account</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Who should stay on Fergus</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Fergus is the right tool for a specific type of trade business. If the following describes your operation, it may well be worth keeping:
            </p>
            <div className="space-y-3">
              {STAY_ON_FERGUS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Reasons UK builders switch from Fergus to The Proper Tool</h2>
            <p className="text-lg text-muted-foreground mb-8">The switch is typically from a field service platform to a tool built specifically for construction quoting.</p>
            <div className="space-y-4">
              {SWITCH_REASONS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-primary/5 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try The Proper Tool free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              UK-built quoting software for residential builders. Generate a professional, fully itemised construction quote from drawings or a description in under 10 minutes.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free, no credit card needed
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · Cancel any time</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Disclaimer:</strong> Competitor pricing and feature information on this page is based on publicly available data and is correct to the best of our knowledge at the time of publication. Prices and features may have changed. We recommend verifying current details directly with Fergus before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Also comparing</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/best/quoting-software-for-uk-builders", label: "Full comparison: all quoting tools for UK builders" },
                { href: "/tradify-alternative", label: "Tradify Alternative" },
                { href: "/simpro-alternative", label: "simPRO Alternative for Small Builders" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="rounded-lg border border-border p-4 text-sm font-medium hover:border-primary hover:text-primary transition-colors"
                >
                  {link.label} →
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
